import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import useStrippedDatasets from './queries/useStrippedDatasets';
import favoriteDatasetFn from './functions/favoriteDatasetPutFn';
import { MAX_FAVORITES_IN_LIST, MAX_RECENT_IN_LIST } from '../config';
import deleteDatasetFn from './functions/deleteDatasetFn';
import { queryClient } from '../setupTanstackQuery';

/**
 * @typedef {object} DatasetConfiguration
 * @property {object[]} fields
 * @property {{
 *  createdDate: string,
 *  resolvedDate: string,
 *  isCrossProject: boolean,
 *  areaPath: string,
 * }} filters
 * @property {{
 *  type: string,
 *  board: string,
 *  teams: string[]
 * }} loadConfig
 * @property {object} statusFlow
 */

/**
 * @typedef {object} Dataset
 * @property {string} name
 * @property {string} ownerId
 * @property {string} subscriptionId
 * @property {string} uuid
 * @property {string} createdDate
 * @property {string} updatedDate
 * @property {DatasetConfiguration} configuration
 * @property {boolean} favorite
 */

/**
 * @typedef {object} UseSavedDatasetsReturns
 * @property {Dataset[]} favoriteDatasets
 * @property {Dataset[]} strippedDatasets
 * @property {Dataset[]} allStrippedDatasets
 * @property {(uuid: string) => promise<void>} favoriteDatasetById
 * @property {boolean} isLoading
 */

/**
 * Backend datasets hook
 * @returns {UseSavedDatasetsReturns}
 */
const useSavedDatasets = () => {
  const {
    account: { id: organizationId },
    project: { id: projectId },
  } = window.VSS.getWebContext();

  const {
    isSuccess: isSuccessRecent,
    data: dataRecent,
    isLoading: isLoadingRecent,
  } = useStrippedDatasets();

  const datasetDeleteMutation = useMutation({
    mutationFn: deleteDatasetFn,
    onSuccess: (_, variables) => {
      const { uuid } = variables;

      queryClient.setQueryData([`datasets-recent-${organizationId}-${projectId}`], (prevState) => {
        if (!prevState) return prevState;
        return prevState.filter((dataset) => dataset.uuid !== uuid);
      });

      console.log('dataset deleted');
    },
    onError: (error) => {
      console.error('Error caught on dataset delete', error);
    },
  });

  const deleteDatasetById = (uuid) => {
    datasetDeleteMutation.mutate({
      uuid,
    });
  };

  const datasetFavoriteMutation = useMutation({
    mutationFn: favoriteDatasetFn,
    onSuccess: (data, variables) => {
      const { uuid } = variables;

      queryClient.setQueryData([`datasets-recent-${organizationId}-${projectId}`], (prevState) => {
        if (!prevState) return prevState;
        const newState = prevState.map((dataset) => ({
          ...dataset,
          favorite: dataset.uuid === uuid ? !dataset.favorite : dataset.favorite,
        }));
        return newState;
      });

      console.log('dataset favorited');
    },
    onError: (error) => {
      console.error('Error caught on dataset favorite', error);
    },
  });

  const favoriteDatasetById = (uuid) => {
    datasetFavoriteMutation.mutate({
      uuid,
    });
  };

  const allStrippedDatasets = useMemo(() => {
    if (!isSuccessRecent) return [];

    const backendDatasets = dataRecent;

    const localDatasets = backendDatasets.map((dataset) => {
      const { favorite = false } = dataset;
      return { ...dataset, favorite };
    });

    // Arrange datasets by lastLoadedDate, start with last loaded
    return [...localDatasets].sort((a, b) => {
      const dateA = new Date(a.lastLoadedDate);
      const dateB = new Date(b.lastLoadedDate);
      return dateB - dateA;
    });
  }, [dataRecent, isSuccessRecent]);

  // Limit datasets to display in list to what is set in config.ts + favorites and not favorites
  const filteredStrippedDatasets = useMemo(() => {
    return allStrippedDatasets.slice(0, MAX_RECENT_IN_LIST);
  }, [allStrippedDatasets]);

  // Limit datasets to display in list to what is set in config.ts + only favorited ones
  const favoriteDatasets = useMemo(() => {
    return allStrippedDatasets
      ?.filter((dataset) => dataset.favorite)
      .slice(0, MAX_FAVORITES_IN_LIST);
  }, [allStrippedDatasets]);

  return {
    deleteDatasetById,
    favoriteDatasetById,
    favoriteDatasets,
    allStrippedDatasets,
    filteredStrippedDatasets,
    isLoading: isLoadingRecent,
  };
};

export default useSavedDatasets;
